import { useSuspenseQuery } from '@tanstack/react-query';
import React, { Suspense } from 'react';
import { Navigate, useParams } from 'react-router-dom';
import { OrderDetail, OrderDetailItem } from '../../types';
import { formatCurrency } from '~/utils/currency';
import { formatDateTime } from '~/utils/date';
import { Divider } from 'antd';
import { api, Order } from '@restaurant/api';
import { ORDER_TYPE_TEXT, PAYMENT_METHOD_TEXT } from '@restaurant/constants';
import dayjs from 'dayjs';
import StatusTag from '../ListOrder/components/StatusTag';
import Print from './components/Print';

const ItemRow = ({ item }: { item: OrderDetailItem }) => {
  return (
    <div className="px-4 py-1">
      <div className="flex justify-between items-center">
        <span className="text-base font-semibold">
          {`${item.quantity} ${item.name}`}
        </span>
        <span>{formatCurrency(item.totalPrice)}</span>
      </div>
      {item.addOns?.map((addOn) => (
        <div className="flex justify-between items-center" key={addOn.menuId}>
          <span>{`${addOn.quantity} ${addOn.name}`}</span>
          <span>{formatCurrency(addOn.totalPrice)}</span>
        </div>
      ))}
      {item.notes ? <i className="text-xs">{item.notes}</i> : null}
    </div>
  );
};

const TotalSection = ({ order }: { order: Order }) => {
  return (
    <div className="flex flex-col gap-1 px-4 py-2 bg-neutral-100">
      <div className="mb-2">
        <div className="flex justify-between items-center">
          <p>Subtotal</p>
          <p>{formatCurrency(order.subtotalPrice || 0)}</p>
        </div>
        <div className="flex justify-between items-center">
          <p>Tax</p>
          <p>{formatCurrency(order.tax?.isApplied ? order.tax.amount : 0)}</p>
        </div>
      </div>
      <div className="flex justify-between items-center text-lg font-semibold">
        <p>Total Harga</p>
        <p>{formatCurrency(order.totalPrice || 0)}</p>
      </div>
    </div>
  );
};

const Content = ({ id }: { id: string }) => {
  const { data: order } = useSuspenseQuery({
    queryKey: ['fetchOrderDetail', id],
    queryFn: () => {
      return api.fetchOrderById(id);
    },
  });

  const orderInfoText = (() => {
    let customerName = order.customerName;
    if (order.tableNumber) {
      customerName = `Meja ${order.tableNumber}`;
      if (order.customerName) {
        customerName += ` (${order.customerName})`;
      }
    }
    return `${ORDER_TYPE_TEXT[order.orderType]} | ${customerName}`;
  })();

  return (
    <div className="py-6 max-w-[600px]">
      <div className="px-4 flex justify-between">
        <div>
          <div className="flex items-center">
            <p className="text-xl mr-2">{`No. Order: ${order.orderNumber}`}</p>
            <StatusTag value={order.status} />
          </div>
          <p>{orderInfoText}</p>
        </div>
        <div>
          <p className="text-neutral-400 text-xs">
            Created {dayjs.unix(order.dateCreated).format('DD MMM YY HH:mm')}
          </p>
        </div>
      </div>
      <Divider className="my-2 py-0" />
      <div style={{ paddingBottom: 100 }}>
        {order.batches.map((batch: any, batchIndex: any) => {
          return (
            <div key={`${batchIndex}`}>
              {batch.map((item: any, itemIndex: any) => (
                <ItemRow
                  item={item}
                  key={`${batchIndex}-${itemIndex}-${item.menuId}`}
                />
              ))}
            </div>
          );
        })}
        {order.tax && order.tax.isApplied && (
          <div className="flex justify-between items-center cursor-pointer mx-4">
            <p>{order.tax.taxType}</p>
            <p>{formatCurrency(order.tax.amount)}</p>
          </div>
        )}
      </div>
      <div>
        <TotalSection order={order} />
        {order.payment ? (
          <div className="p-4 flex flex-col gap-1 bg-white">
            <div className="flex justify-between items-center mb-1">
              <p>Rounding</p>
              <p>{formatCurrency(order.payment.roundingAmount)}</p>
            </div>
            <div className="flex flex-col gap-2">
              {order.payment?.details?.map((item, index) => (
                <div className="flex justify-between items-center">
                  <div>
                    <p>
                      #{index + 1} {PAYMENT_METHOD_TEXT[item.method]}
                    </p>
                    <p className="text-[10px]">
                      {formatDateTime(item.timestamp)}
                    </p>
                  </div>
                  <p>{formatCurrency(item.amount)}</p>
                </div>
              ))}
            </div>
          </div>
        ) : (
          <div className="px-4 py-4">
            <p>Belum ada pembayaran</p>
          </div>
        )}
      </div>
      <Divider />
      <Print id={order.orderId} />
    </div>
  );
};

const OrderDetailPage = () => {
  const { id } = useParams();
  if (!id) return <Navigate to="/order" />;
  return (
    <Suspense>
      <Content id={id} />
    </Suspense>
  );
};

export default OrderDetailPage;
